import React from "react";
import {
  Box,
  Card,
  Typography,
} from "@mui/material";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import ApprovalIcon from "@mui/icons-material/Approval";
import PanToolIcon from "@mui/icons-material/PanTool";
import SmsFailedIcon from "@mui/icons-material/SmsFailed";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useNavigate } from "react-router-dom";

export default function StatusCards({ stats }) {
  const navigate = useNavigate();

  const statusCards = [
    {
      label: "Pending",
      value: stats?.totalPending ?? 0,
      icon: <AutorenewIcon />,
    },
    {
      label: "Applied",
      value: stats?.totalApplied ?? 0,
      icon: <ApprovalIcon />,
    },
    {
      label: "Waiting",
      value: stats?.totalWaiting ?? 0,
      icon: <PanToolIcon />,
    },
    {
      label: "Success",
      value: stats?.totalSuccess ?? 0,
      icon: <DoneAllIcon />,
    },
    {
      label: "Failed",
      value: stats?.totalFailed ?? 0,
      icon: <SmsFailedIcon />,
    },
    {
      label: "Rejected",
      value: stats?.totalRejected ?? 0,
      icon: <ThumbDownIcon />,
    },
  ];

  const handleStatusClick = () => {
    navigate("/payments/status");
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          width: "100%",
          minWidth: 0,

          /*
           * Mobile
           * 2 cards per row
           */
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            md: "repeat(6, minmax(0, 1fr))",
          },

          gap: {
            xs: 1,
            sm: 1.5,
            md: 2,
          },
        }}
      >
        {statusCards.map((card) => (
          <Card
            key={card.label}
            onClick={handleStatusClick}
            sx={{
              width: "100%",
              minWidth: 0,

              /*
               * Equal card height
               */
              minHeight: {
                xs: 100,
                sm: 105,
                md: 110,
              },

              height: "100%",

              p: {
                xs: 1.25,
                sm: 1.5,
                md: 2,
              },

              boxSizing: "border-box",

              borderRadius: {
                xs: 1.5,
                sm: 2,
                md: 2,
              },

              boxShadow: 0,

              cursor: "pointer",

              display: "flex",
              flexDirection: "column",

              transition:
                "transform 0.2s ease, box-shadow 0.2s ease",

              "&:hover": {
                transform: {
                  xs: "none",
                  sm: "translateY(-2px)",
                },

                boxShadow: {
                  xs: 0,
                  sm: 2,
                },
              },
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "center",
                  sm: "flex-end",
                },

                alignItems: "center",

                width: "100%",

                mb: {
                  xs: 0.5,
                  sm: 0.5,
                },

                "& svg": {
                  color: "#F9B572",

                  fontSize: {
                    xs: 21,
                    sm: 23,
                    md: 25,
                  },

                  flexShrink: 0,
                },
              }}
            >
              {card.icon}
            </Box>

            {/* Number + Label */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",

                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                },

                justifyContent: "center",

                flex: 1,

                width: "100%",

                minWidth: 0,

                textAlign: {
                  xs: "center",
                  sm: "left",
                },
              }}
            >
              {/* Number */}
              <Typography
                component="div"
                sx={{
                  fontSize: {
                    xs: "1rem",
                    sm: "1.15rem",
                    md: "1.25rem",
                  },

                  fontWeight: 600,

                  lineHeight: 1.2,

                  color: "text.primary",

                  width: "100%",

                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {card.value}
              </Typography>

              {/* Status */}
              <Typography
                component="div"
                sx={{
                  mt: 0.3,

                  fontSize: {
                    xs: "0.75rem",
                    sm: "0.8rem",
                    md: "0.875rem",
                  },

                  fontWeight: 400,

                  lineHeight: 1.25,

                  color: "text.secondary",

                  /*
                   * IMPORTANT:
                   * Don't use ellipsis here.
                   * Status names should always be visible.
                   */
                  whiteSpace: "normal",

                  overflow: "visible",

                  textOverflow: "unset",

                  wordBreak: "normal",

                  overflowWrap: "normal",

                  width: "100%",

                  textAlign: {
                    xs: "center",
                    sm: "left",
                  },
                }}
              >
                {card.label}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>
    </Box>
  );
}